import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Save, Search, Send, Star, Trash2, X } from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import {
  createEmailSenderProfile,
  deleteEmailSenderProfile,
  getEmailSenderProfiles,
  testEmailSenderProfile,
  updateEmailSenderProfile,
} from '../../Services/EmailAdminService';
import { EmailSenderProfile, EmailSenderProfilePayload } from '../../Types/EmailAdminType';

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';
const badgeBase =
  'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider';

const emptyForm: EmailSenderProfilePayload & { id?: string } = {
  name: '',
  fromEmail: '',
  fromName: '',
  isDefault: false,
  status: 'ACTIVE',
  smtpHost: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  smtpAuth: true,
  smtpStartTls: true,
};

function EmailSenderProfileManager() {
  const [profiles, setProfiles] = useState<EmailSenderProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<EmailSenderProfilePayload & { id?: string }>({ ...emptyForm });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getEmailSenderProfiles({ page: 0, size: 100 });
      if (res.success && res.data) {
        setProfiles(res.data.content);
      } else {
        showAlert('error', 'Load failed', res.message || 'Could not load sender profiles.');
      }
    } catch {
      showAlert('error', 'Load failed', 'Could not load sender profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.fromEmail.toLowerCase().includes(q) ||
        p.fromName.toLowerCase().includes(q)
    );
  }, [profiles, search]);

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditing(false);
    setTestEmail('');
  };

  const startEdit = (p: EmailSenderProfile) => {
    setForm({
      id: p.id,
      name: p.name,
      fromEmail: p.fromEmail,
      fromName: p.fromName,
      isDefault: p.isDefault,
      status: p.status,
      smtpHost: p.smtpHost ?? '',
      smtpPort: p.smtpPort ?? 587,
      smtpUsername: p.smtpUsername ?? '',
      smtpPassword: '',
      smtpAuth: p.smtpAuth ?? true,
      smtpStartTls: p.smtpStartTls ?? true,
    });
    setEditing(true);
    setAlert(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this sender profile?')) return;
    const res = await deleteEmailSenderProfile(id);
    if (res.success) {
      showAlert('success', 'Deleted', 'Sender profile removed.');
      if (form.id === id) resetForm();
      await load();
    } else {
      showAlert('error', 'Delete failed', res.message || 'Please try again.');
    }
  };

  const buildPayload = (): EmailSenderProfilePayload => {
    const payload: EmailSenderProfilePayload = {
      name: form.name.trim(),
      fromEmail: form.fromEmail.trim(),
      fromName: form.fromName.trim(),
      isDefault: form.isDefault,
      status: form.status,
      smtpHost: form.smtpHost?.trim(),
      smtpPort: form.smtpPort,
      smtpUsername: form.smtpUsername?.trim(),
      smtpPassword: form.smtpPassword,
      smtpAuth: form.smtpAuth,
      smtpStartTls: form.smtpStartTls,
    };
    if (!payload.smtpPassword) {
      delete payload.smtpPassword;
    }
    return payload;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.fromEmail.trim() || !form.fromName.trim()) {
      showAlert('error', 'Incomplete', 'Name, From email and From name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = editing
        ? await updateEmailSenderProfile(form.id!, payload)
        : await createEmailSenderProfile(payload);
      if (res.success) {
        showAlert('success', 'Saved', editing ? 'Sender profile updated.' : 'Sender profile created.');
        resetForm();
        await load();
      } else {
        showAlert('error', 'Save failed', res.message || 'Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!form.fromEmail.trim() || !form.fromName.trim()) {
      showAlert('error', 'Incomplete', 'From email and From name are required to test.');
      return;
    }
    if (!form.smtpHost?.trim() || !form.smtpPort) {
      showAlert('error', 'Incomplete', 'SMTP host and port are required to test.');
      return;
    }
    if (!testEmail.trim()) {
      showAlert('error', 'Incomplete', 'Enter a test recipient e-mail.');
      return;
    }
    setTesting(true);
    try {
      const payload = buildPayload();
      const res = await testEmailSenderProfile(payload, testEmail.trim());
      if (res.success) {
        showAlert('success', 'Test sent', `A test e-mail was sent to ${testEmail.trim()}.`);
      } else {
        showAlert('error', 'Test failed', res.message || 'Please check the SMTP settings.');
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
      <header className="mb-4">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Sender profiles</h2>
        <p className="text-[11px] font-semibold text-slate-500">
          Configure the From email/name and SMTP credentials admins can use. When a profile is
          selected in the composer, e-mail is sent through its SMTP settings.
        </p>
      </header>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      <form onSubmit={handleSave} className="mb-6 rounded-2xl border border-slate-800 bg-slate-950/30 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelClass}>Profile name</label>
            <input
              className={fieldClass}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Marketing Team"
            />
          </div>
          <div>
            <label className={labelClass}>From email</label>
            <input
              type="email"
              className={fieldClass}
              value={form.fromEmail}
              onChange={(e) => setForm((f) => ({ ...f, fromEmail: e.target.value }))}
              placeholder="team@nexgent.com"
            />
          </div>
          <div>
            <label className={labelClass}>From name</label>
            <input
              className={fieldClass}
              value={form.fromName}
              onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))}
              placeholder="Nexgent Team"
            />
          </div>

          <div className="sm:col-span-2 mt-2 border-t border-slate-800 pt-3">
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-cyan-400">
              SMTP credentials for this sender
            </p>
          </div>
          <div>
            <label className={labelClass}>SMTP host</label>
            <input
              className={fieldClass}
              value={form.smtpHost}
              onChange={(e) => setForm((f) => ({ ...f, smtpHost: e.target.value }))}
              placeholder="smtp.gmail.com"
            />
          </div>
          <div>
            <label className={labelClass}>SMTP port</label>
            <input
              type="number"
              className={fieldClass}
              value={form.smtpPort}
              onChange={(e) => setForm((f) => ({ ...f, smtpPort: parseInt(e.target.value || '0', 10) }))}
              placeholder="587"
            />
          </div>
          <div>
            <label className={labelClass}>SMTP username</label>
            <input
              className={fieldClass}
              value={form.smtpUsername}
              onChange={(e) => setForm((f) => ({ ...f, smtpUsername: e.target.value }))}
              placeholder="team@nexgent.com"
            />
          </div>
          <div>
            <label className={labelClass}>SMTP password {editing && '(leave blank to keep saved)'}</label>
            <input
              type="password"
              className={fieldClass}
              value={form.smtpPassword}
              onChange={(e) => setForm((f) => ({ ...f, smtpPassword: e.target.value }))}
              placeholder="App password"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2 transition hover:border-cyan-500/30">
              <input
                type="checkbox"
                checked={form.smtpAuth}
                onChange={(e) => setForm((f) => ({ ...f, smtpAuth: e.target.checked }))}
                className="h-4 w-4 accent-cyan-500"
              />
              <span className="text-xs font-semibold text-slate-300">Auth</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2 transition hover:border-cyan-500/30">
              <input
                type="checkbox"
                checked={form.smtpStartTls}
                onChange={(e) => setForm((f) => ({ ...f, smtpStartTls: e.target.checked }))}
                className="h-4 w-4 accent-cyan-500"
              />
              <span className="text-xs font-semibold text-slate-300">STARTTLS</span>
            </label>
          </div>

          <div className="flex items-center gap-3 sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/50 px-3 py-2 transition hover:border-cyan-500/30">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                className="h-4 w-4 accent-cyan-500"
              />
              <span className="text-sm font-semibold text-slate-300">Use as default profile</span>
            </label>
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as 'ACTIVE' | 'INACTIVE' }))
              }
              className={`${fieldClass} appearance-none`}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {editing ? 'Update Profile' : 'Add Profile'}
          </button>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2 text-sm font-black text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-50"
            >
              <X size={16} />
              Cancel
            </button>
          )}
          <div className="ml-auto flex flex-1 items-center gap-2 sm:flex-initial">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Test recipient e-mail"
              className="min-w-[180px] flex-1 rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
            />
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className="inline-flex items-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-sm font-black text-cyan-400 transition hover:bg-cyan-500/20 disabled:opacity-50"
            >
              {testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Test
            </button>
          </div>
        </div>
      </form>

      <div className="mb-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search profiles…"
            className="w-full rounded-xl border border-slate-800 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-950/30 p-4 text-sm font-semibold text-slate-500">
          {search ? 'No profiles match your search.' : 'No sender profiles configured yet.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="flex flex-col gap-2 rounded-2xl border border-slate-800 bg-slate-950/30 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-bold text-slate-200">{p.name}</span>
                  {p.isDefault && (
                    <span className={`${badgeBase} bg-amber-500/10 text-amber-400`}>
                      <Star size={10} className="mr-1" />
                      Default
                    </span>
                  )}
                  <span
                    className={`${badgeBase} ${
                      p.status === 'ACTIVE'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-slate-700/30 text-slate-400'
                    }`}
                  >
                    {p.status}
                  </span>
                  {p.smtpHost && (
                    <span className={`${badgeBase} bg-cyan-500/10 text-cyan-400`}>
                      {p.smtpHost}:{p.smtpPort}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs font-semibold text-slate-400">
                  {p.fromName} &lt;{p.fromEmail}&gt;
                </div>
              </div>
              <div className="flex items-center gap-2 sm:ml-4">
                <button
                  onClick={() => startEdit(p)}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 px-2.5 py-1.5 text-xs font-bold text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400"
                >
                  <Pencil size={12} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 px-2.5 py-1.5 text-xs font-bold text-slate-300 transition hover:border-rose-500/40 hover:text-rose-400"
                >
                  <Trash2 size={12} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default EmailSenderProfileManager;
